import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import FullWidthContainer from "../../components/FullWidthContainer";
import Button from "../../components/ui-lib/buttons/Button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import LessonViewer from "../../components/scheduler/LessonViewer";
import { generateComponentSchedule } from "../../helpers/utils/calendar.util";
import ApplicationCard from "../../components/vacancy/ApplicationCard";
import Scheduler from "../../components/scheduler/Scheduler";
import { ViewModeWeek } from "../../components/ui-lib/scheduler/viewModeWeek";
import { ViewModeFullWeek } from "../../components/ui-lib/scheduler/viewModeFullWeek";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../auth/permissions";
import { useVacancyApi } from "../../apis/vacancy-api";
import { Vacancy } from "../../models/vacancy/vacancy";
import VacancyDetailDisplay from "./VacancyDetailDisplay";
import { useState } from "react";
import { Teacher } from "../../models/teacher/teacher";
import { useGlobalState } from "../../states/global-state/global.state";
import List from "../../components/ui-lib/List";
import AcceptTeacherApplicationDialog, {
  AcceptTeacherDialogResultProps,
} from "../vacancy/AcceptTeacherApplicationDialog";
import { CalendarDaysIcon, TrashIcon } from "@heroicons/react/24/outline";
import { NidessoQueryParams } from "../../components/router/query-params";

function VacancyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getVacancy,
    deleteVacancy,
    acceptVacancyApplication,
    declineVacancyApplication,
  } = useVacancyApi();
  const { setIsLoading } = useGlobalState();

  if (!id) {
    throw Error("No vacancy id provided");
  }

  const { data: vacancy, refetch: fetchVacancy } = useQuery(
    ["vacancy", id],
    () => getVacancy(id),
  );
  const { hasPermissions } = usePermissions();
  const [acceptTeacherApplication, setAcceptTeacherApplication] = useState<
    Teacher | undefined
  >(undefined);

  const onDelete = useMutation({
    mutationFn: (v: Vacancy) => deleteVacancy(v.id, v.school.id),
    onSuccess: () => navigate(-1),
  });

  const acceptMutation = useMutation({
    mutationFn: ({
      teacherId,
      lessonIds,
    }: {
      teacherId: string;
      lessonIds: string[];
    }) =>
      acceptVacancyApplication(
        vacancy!.school.id,
        teacherId,
        vacancy!.id,
        lessonIds,
      ),
  });

  const declineMutation = useMutation({
    mutationFn: ({
      teacherId,
      lessonIds,
    }: {
      teacherId: string;
      lessonIds: string[];
    }) =>
      declineVacancyApplication(
        vacancy!.school.id,
        teacherId,
        vacancy!.id,
        lessonIds,
      ),
  });

  const onDialogCompleted = async (result?: AcceptTeacherDialogResultProps) => {
    setAcceptTeacherApplication(undefined);
    if (!result) return;
    let calls: Promise<unknown>[] = [];

    if (acceptTeacherApplication && result.acceptedLessons?.length) {
      calls.push(
        acceptMutation.mutateAsync({
          teacherId: acceptTeacherApplication?.id,
          lessonIds: result.acceptedLessons.map((l) => l.id),
        }),
      );
    }

    if (acceptTeacherApplication && result.declinedLessons?.length) {
      calls.push(
        declineMutation.mutateAsync({
          teacherId: acceptTeacherApplication?.id,
          lessonIds: result.declinedLessons.map((l) => l.id),
        }),
      );
    }

    setIsLoading(true);
    await Promise.allSettled(calls);
    await fetchVacancy();
    setIsLoading(false);
  };

  const onUpdateVacancy = () => {
    navigate(`/vacancy/${vacancy?.id}/update`, {
      state: { [NidessoQueryParams.Vacancy]: vacancy },
    });
  };

  return (
    <FullWidthContainer className="bg-nid_grey">
      {vacancy && (
        <div className="flex flex-col space-y-8">
          <div className="flex justify-between">
            <Button
              className="self-start rounded-full md:self-center"
              onClick={() => navigate(-1)}
            >
              <ArrowLeftIcon
                className="m-auto h-5 w-5 text-white"
                aria-hidden="true"
              ></ArrowLeftIcon>
            </Button>
            <div className="flex space-x-2">
              <Button className="space-x-2" onClick={onUpdateVacancy}>
                <CalendarDaysIcon className="size-5" />
                <span>Editieren</span>
              </Button>
              {hasPermissions([PERMISSIONS.CanDeleteVacancy]) && (
                <Button theme="error" onClick={() => onDelete.mutate(vacancy)}>
                  <TrashIcon className="size-5" />
                </Button>
              )}
            </div>
          </div>
          <div className="space-x-4 md:flex">
            <div className="flex-grow">
              <div className="mb-8 flex items-center">
                <LessonViewer
                  className="mr-8"
                  lessons={generateComponentSchedule(
                    vacancy.schedule,
                    new Date(vacancy.startDate),
                    new Date(vacancy.endDate),
                    vacancy.lessons,
                  )}
                  startDate={new Date(vacancy.startDate)}
                  viewMode={new ViewModeFullWeek(true)}
                  neutralColor="bg-white"
                ></LessonViewer>
                <span className="font-bold">
                  {vacancy.startDate.toLocaleDateString()} -{" "}
                  {vacancy.endDate.toLocaleDateString()}
                </span>
              </div>
              <VacancyDetailDisplay vacancy={vacancy} />
              <Scheduler
                className="mt-8 lg:max-w-[75%]"
                lessons={generateComponentSchedule(
                  vacancy.schedule,
                  new Date(vacancy.startDate),
                  new Date(vacancy.endDate),
                  vacancy.lessons,
                )}
                startDate={new Date(vacancy.startDate)}
                setValue={() => {}}
                viewMode={new ViewModeWeek(true)}
                isReadOnly={true}
                duration={vacancy.schedule.duration}
              ></Scheduler>
            </div>
            {(!!vacancy.teacherAccepted?.length ||
              !!vacancy.teacherApplications?.length) && (
              <div className="space-y-4 md:w-[30%] xl:w-[20%]">
                <List
                  items={vacancy.teacherAccepted ?? []}
                  title="Akzeptierte Vertretungen"
                  template={(teacher) => (
                    <ApplicationCard
                      key={`application_card_${teacher.id}_accepted`}
                      teacher={teacher}
                      vacancy={vacancy}
                      clicked={() => setAcceptTeacherApplication(teacher)}
                    ></ApplicationCard>
                  )}
                ></List>
                <List
                  items={vacancy.teacherApplications ?? []}
                  title="Bewerbungen"
                  template={(teacher) => (
                    <ApplicationCard
                      key={`application_card_${teacher.id}_applied`}
                      teacher={teacher}
                      vacancy={vacancy}
                      clicked={() => setAcceptTeacherApplication(teacher)}
                    ></ApplicationCard>
                  )}
                ></List>
              </div>
            )}
          </div>
        </div>
      )}
      {acceptTeacherApplication && vacancy && (
        <AcceptTeacherApplicationDialog
          onClose={(result) => onDialogCompleted(result)}
          open={!!acceptTeacherApplication}
          teacherApplication={acceptTeacherApplication}
          vacancy={vacancy}
        />
      )}
    </FullWidthContainer>
  );
}

export default VacancyDetails;
